export const BuildView = {
  Index: "INDEX",
  Summary: "SUMMARY",
  StepUploads: "STEP_UPLOADS",
  Waterfall: "WATERFALL",
  Table: "TABLE",
  Canvas: "CANVAS",
  List: "LIST",
  Tests: "TESTS",
  Annotations: "ANNOTATIONS",
} as const;

export type BuildView = (typeof BuildView)[keyof typeof BuildView];

/**
 * The subset of BuildView values that represent steps display modes.
 * Every StepsDisplayMode is a valid BuildView, so no mapping is needed.
 */
export const StepsDisplayMode = {
  Canvas: BuildView.Canvas,
  Table: BuildView.Table,
  List: BuildView.List,
  Waterfall: BuildView.Waterfall,
} as const satisfies Partial<typeof BuildView>;

export type StepsDisplayMode = (typeof StepsDisplayMode)[keyof typeof StepsDisplayMode];

export const STEPS_DISPLAY_MODES: ReadonlySet<BuildView> = new Set(Object.values(StepsDisplayMode));
/**
 * The subset of BuildView values that represent summary display modes.
 * Every SummaryDisplayMode is a valid BuildView, so no mapping is needed.
 */
export const SummaryDisplayMode = {
  Summary: BuildView.Summary,
  StepUploads: BuildView.StepUploads,
} as const satisfies Partial<typeof BuildView>;

export type SummaryDisplayMode = (typeof SummaryDisplayMode)[keyof typeof SummaryDisplayMode];

export const SUMMARY_DISPLAY_MODES: ReadonlySet<BuildView> = new Set(
  Object.values(SummaryDisplayMode),
);

/**
 * Resolve the effective steps display mode by applying feature-flag fallbacks.
 *
 * If the requested mode is disabled by a feature flag, falls back to Table.
 * Unknown modes fall back to Canvas.
 */
export function resolveStepsDisplayMode(mode: StepsDisplayMode): StepsDisplayMode {
  switch (mode) {
    case StepsDisplayMode.Canvas:
      return Features.DisableStepsCanvasView ? StepsDisplayMode.Table : mode;
    case StepsDisplayMode.Table:
      return mode;
    case StepsDisplayMode.List:
      return !Features.BuildPageListView ? StepsDisplayMode.Table : mode;
    case StepsDisplayMode.Waterfall:
      return Features.DisableStepsWaterfallView ? StepsDisplayMode.Table : mode;
    default:
      return StepsDisplayMode.Canvas;
  }
}

export const ANNOTATIONS_VIEWS: ReadonlySet<BuildView> = new Set([BuildView.Annotations]);

export const TESTS_VIEWS: ReadonlySet<BuildView> = new Set([BuildView.Tests]);
